import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";

import { supabase } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";

type DealType = { id: number; name: string; color: string };
type StageLimit = {
  id?: number;
  stage: string;
  deal_type_id: number;
  max_days: number;
};

export const StageLimitsConfig = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { accountStages } = useConfigurationContext();
  const [limits, setLimits] = useState<Record<string, Record<number, number>>>(
    {},
  );
  const [dirty, setDirty] = useState(false);

  const { data: dealTypes } = useQuery({
    queryKey: ["deal_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deal_types")
        .select("id, name, color")
        .order("sort_order");
      if (error) throw error;
      return data as DealType[];
    },
  });

  const { data: existingLimits } = useQuery({
    queryKey: ["stage_time_limits"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stage_time_limits")
        .select("*");
      if (error) throw error;
      return data as StageLimit[];
    },
  });

  useEffect(() => {
    if (existingLimits) {
      const map: Record<string, Record<number, number>> = {};
      for (const l of existingLimits) {
        if (!map[l.stage]) map[l.stage] = {};
        map[l.stage][l.deal_type_id] = l.max_days;
      }
      setLimits(map);
    }
  }, [existingLimits]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const rows: { stage: string; deal_type_id: number; max_days: number }[] =
        [];
      for (const stage of Object.keys(limits)) {
        for (const [dtId, maxDays] of Object.entries(limits[stage])) {
          rows.push({
            stage,
            deal_type_id: Number(dtId),
            max_days: maxDays,
          });
        }
      }
      const { error } = await supabase
        .from("stage_time_limits")
        .upsert(rows, { onConflict: "stage,deal_type_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      notify("Stage limits saved", { type: "success" });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["stage_time_limits"] });
    },
    onError: () => notify("Failed to save", { type: "error" }),
  });

  const handleChange = (
    stage: string,
    dealTypeId: number,
    value: string,
  ) => {
    const num = value === "" ? 0 : Number(value);
    if (Number.isNaN(num)) return;
    setLimits((prev) => ({
      ...prev,
      [stage]: { ...prev[stage], [dealTypeId]: num },
    }));
    setDirty(true);
  };

  // Only show account stages (not outreach — outreach doesn't have deal types)
  const stages = accountStages.filter((s) => s.value !== "lost");

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Set maximum days per stage for each deal type. Deals exceeding these
        limits will show an overdue warning on their pipeline card.
      </p>

      {dealTypes && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left py-2 pr-3 text-xs font-medium text-muted-foreground">
                  Stage
                </th>
                {dealTypes.map((dt) => (
                  <th
                    key={dt.id}
                    className="text-left py-2 px-2 text-xs font-medium"
                  >
                    <span
                      className="px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: dt.color }}
                    >
                      {dt.name}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stages.map((stage) => (
                <tr key={stage.value}>
                  <td className="py-2 pr-3 font-medium">{stage.label}</td>
                  {dealTypes.map((dt) => (
                    <td key={dt.id} className="py-2 px-2">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          className="h-7 w-16 rounded-md border bg-background px-2 text-sm"
                          value={limits[stage.value]?.[dt.id] ?? ""}
                          placeholder="—"
                          onChange={(e) =>
                            handleChange(stage.value, dt.id, e.target.value)
                          }
                        />
                        <span className="text-xs text-muted-foreground">
                          days
                        </span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dirty && (
        <Button
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save
        </Button>
      )}
    </div>
  );
};
